import React from 'react';
import ReactDOM from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { registerSW } from 'virtual:pwa-register';

import { routeTree } from './routeTree.gen';
import { queryClient, useAuthStore } from '@kapture/client-core';
import { initDb } from './lib/db/init';
import { setQueryInvalidator, startSyncTriggers, syncOnStart } from '@kapture/sync-core';
import { loadTemplates } from '@kapture/domain';
import { installWebPlatform } from './platform/boot';
import { ensurePersistentStorage } from '@/lib/device/permissions';
import './index.css';

// Register the service worker — auto-update with no reload prompt for now.
registerSW({ immediate: true });

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Keep the query cache authoritative; don't let the router double-fetch.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// Register the router instance for type safety on Link/useNavigate/useParams.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root')!);

function Splash({ error }: { error?: string }) {
  return (
    <div className="bg-surface-2 text-text-dim grid min-h-dvh place-items-center font-sans text-sm">
      <div className="text-center">
        <div className="bg-ink text-brand mx-auto mb-4 grid h-12 w-12 place-items-center rounded-xl">
          <span className="font-mono text-[11px] font-bold tracking-tight">KAP</span>
        </div>
        {error ? (
          <>
            <div className="text-danger text-base font-semibold">Storage unavailable</div>
            <div className="mt-2 max-w-xs text-xs">{error}</div>
          </>
        ) : (
          <>
            <div className="text-ink text-base font-semibold">Kapture</div>
            <div className="mt-1">Opening your offline storage…</div>
          </>
        )}
      </div>
    </div>
  );
}

root.render(<Splash />);

// Tell the shared engine what machine it is on. Must precede `initDb()`:
// everything below this line reaches SQLite, media and the network through
// the registry these adapters populate.
installWebPlatform();

// Ask the browser to stop treating this origin's storage as evictable, before
// anything writes to it. Deliberately not awaited: on Chrome/Android this is
// granted silently and instantly, and on the engines where it prompts, boot
// must not wait on the answer. `ensurePersistentStorage` asks at most once
// ever, so an inspector who declines is not met with a dialog on every cold
// start. The result is logged rather than blocking — a denial degrades
// durability, it does not break capture.
void ensurePersistentStorage().then((state) => {
  if (state !== 'granted') {
    console.warn(
      `[kapture] persistent storage not granted (${state}) — the browser may evict ` +
        'unsynced captures under storage pressure. Sync sooner.',
    );
  }
});

// Boot the database (migrations + first-launch seed) before hydrating the
// router so the very first SessionHubPage render already has rows to show.
// Auth hydration follows, because restoring a session backfills org_id on
// local rows and therefore needs the schema to exist.
initDb()
  // Before the router renders. Form screens read templates synchronously, so
  // the cache has to be warm by first paint or the first render falls back to
  // the seed and only corrects itself on the next pull.
  .then(() => loadTemplates())
  .then(() => useAuthStore.getState().hydrate())
  .then(() => {
    // Ambient triggers: foreground-after-a-minute, radio-came-back, and a
    // push whenever new work is enqueued. Lives for the page's lifetime, so
    // the teardown is intentionally unused.
    // Let a finished pull refresh the screens. Without this an ambient sync
    // rewrites SQLite and every view keeps its pre-pull cache, so the sync
    // looks like it did nothing.
    setQueryInvalidator(() => {
      // A pull can bring a new or edited template, and the form screens read
      // the cache rather than the table. Refreshing it here is what makes a
      // template authored in the web builder show up on the phone without a
      // release — which is the entire vertical-pack claim.
      void loadTemplates();
      void queryClient.invalidateQueries();
    });
    startSyncTriggers();
    // And sync once now. `startSyncTriggers` only subscribes; without this a
    // cold start pushed nothing and pulled nothing, so a relaunched app sat
    // on queued work and a new device showed an empty inbox.
    syncOnStart();
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {import.meta.env.DEV && import.meta.env['VITE_QUERY_DEVTOOLS'] === '1' && (
            <ReactQueryDevtools buttonPosition="top-left" />
          )}
        </QueryClientProvider>
      </React.StrictMode>,
    );
  })
  .catch((err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[kapture] DB init failed', err);
    root.render(<Splash error={message} />);
  });
